import { DEFAULT_BODY_PART_WEIGHTS, DEFAULT_SCORING_CONFIG, } from "./config.js";
import { angleDifference, extractMotionFeatures, extractPoseFeatures, interpolateLandmarksAt, } from "./features.js";
import { mirrorLandmarks } from "./pose.js";
/**
 * Pose scoring.
 *
 * Every function here is pure and deterministic: the same inputs always give
 * the same score, which is what makes the tuning in M4 meaningful and the
 * tests trustworthy.
 */
/**
 * Angular error, in radians, at which a joint scores about 0.5.
 *
 * Chosen so that a visibly wrong limb is clearly penalised while ordinary
 * human variation is not. About 34 degrees.
 */
const ANGLE_TOLERANCE_RAD = 0.6;
/** Positional error, in body-scale units, at which a limb scores about 0.5. */
const POSITION_TOLERANCE = 0.35;
/** Velocity error, in body-scale units per second, scoring about 0.5. */
const VELOCITY_TOLERANCE = 1.2;
/**
 * Smooth similarity falloff.
 *
 * A Gaussian rather than a threshold: dancing is continuous, so the score
 * should degrade gradually as the pose drifts instead of flipping between
 * right and wrong at an arbitrary cutoff.
 */
function gaussianSimilarity(error, tolerance) {
    const ratio = error / tolerance;
    return Math.exp(-0.5 * ratio * ratio);
}
function pointDistance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}
/** Mean of the given values, or `fallback` when there are none. */
function mean(values, fallback = 0) {
    if (values.length === 0) {
        return fallback;
    }
    let total = 0;
    for (const value of values) {
        total += value;
    }
    return total / values.length;
}
/** Similarity of one body part, in [0, 1]. */
function scoreArm(player, reference, side) {
    const elbowKey = side === "left" ? "leftElbow" : "rightElbow";
    const shoulderKey = side === "left" ? "leftShoulder" : "rightShoulder";
    const wristKey = side === "left" ? "leftWrist" : "rightWrist";
    const scores = [
        gaussianSimilarity(Math.abs(angleDifference(player.angles[elbowKey], reference.angles[elbowKey])), ANGLE_TOLERANCE_RAD),
        // Wrist position is relative to the shoulder centre, so arm scoring does
        // not need visible hips or ankles.
        gaussianSimilarity(pointDistance(player.positions[wristKey], reference.positions[wristKey]), POSITION_TOLERANCE),
    ];
    if (player.confidence.torso > 0 && reference.confidence.torso > 0) {
        scores.push(gaussianSimilarity(Math.abs(angleDifference(player.angles[shoulderKey], reference.angles[shoulderKey])), ANGLE_TOLERANCE_RAD));
    }
    return mean(scores);
}
function scoreLeg(player, reference, side) {
    const hipKey = side === "left" ? "leftHip" : "rightHip";
    const kneeKey = side === "left" ? "leftKnee" : "rightKnee";
    const ankleKey = side === "left" ? "leftAnkle" : "rightAnkle";
    const scores = [
        gaussianSimilarity(Math.abs(angleDifference(player.angles[kneeKey], reference.angles[kneeKey])), ANGLE_TOLERANCE_RAD),
        gaussianSimilarity(pointDistance(player.positions[ankleKey], reference.positions[ankleKey]), POSITION_TOLERANCE),
    ];
    if (player.confidence.torso > 0 && reference.confidence.torso > 0) {
        scores.push(gaussianSimilarity(Math.abs(angleDifference(player.angles[hipKey], reference.angles[hipKey])), ANGLE_TOLERANCE_RAD));
    }
    return mean(scores);
}
function scoreTorso(player, reference) {
    return mean([
        gaussianSimilarity(Math.abs(angleDifference(player.orientation.shoulderLine, reference.orientation.shoulderLine)), ANGLE_TOLERANCE_RAD),
        gaussianSimilarity(Math.abs(angleDifference(player.orientation.hipLine, reference.orientation.hipLine)), ANGLE_TOLERANCE_RAD),
        gaussianSimilarity(Math.abs(angleDifference(player.orientation.torsoLean, reference.orientation.torsoLean)), ANGLE_TOLERANCE_RAD),
    ]);
}
/** Similarity of movement, in [0, 1]. */
export function scoreMotion(player, reference) {
    const keys = ["leftWrist", "rightWrist", "leftAnkle", "rightAnkle"];
    let total = 0;
    let confidenceSum = 0;
    for (const key of keys) {
        const confidence = Math.min(player.confidence[key], reference.confidence[key]);
        if (confidence < DEFAULT_SCORING_CONFIG.minVisibility) {
            continue;
        }
        total +=
            gaussianSimilarity(pointDistance(player[key], reference[key]), VELOCITY_TOLERANCE) *
                confidence;
        confidenceSum += confidence;
    }
    return confidenceSum > 0 ? total / confidenceSum : 0;
}
/**
 * Compares two poses, part by part.
 *
 * Parts the model could barely see contribute proportionally less, so a limb
 * hidden behind the body cannot drag the score down as if the player had moved
 * it wrongly.
 */
export function scoreStaticPose(player, reference, weights = DEFAULT_BODY_PART_WEIGHTS, minVisibility = DEFAULT_SCORING_CONFIG.minVisibility) {
    const parts = {
        leftArm: scoreArm(player, reference, "left"),
        rightArm: scoreArm(player, reference, "right"),
        leftLeg: scoreLeg(player, reference, "left"),
        rightLeg: scoreLeg(player, reference, "right"),
        torso: scoreTorso(player, reference),
    };
    // Confidence combines both sides: a part is only trustworthy when it was
    // visible in the player's capture and in the reference.
    const confidenceFor = (key) => {
        const confidence = Math.min(player.confidence[key], reference.confidence[key]);
        return confidence >= minVisibility ? confidence : 0;
    };
    const confidences = {
        leftArm: confidenceFor("leftArm"),
        rightArm: confidenceFor("rightArm"),
        leftLeg: confidenceFor("leftLeg"),
        rightLeg: confidenceFor("rightLeg"),
        torso: confidenceFor("torso"),
    };
    // Weighted geometric mean rather than arithmetic.
    //
    // With an arithmetic mean, one completely wrong limb costs only its own
    // weight: a player holding an arm in entirely the wrong place still scored
    // ~87, comfortably inside "Great". The geometric mean makes parts multiply,
    // so a part that is badly wrong pulls the whole score down, and a player who
    // is uniformly close beats one who nails three parts and ignores two. That
    // matches how a person judges whether a dance was copied.
    //
    // Scores are floored before the log: a zero would otherwise annihilate the
    // product, making every other part irrelevant.
    const MIN_PART_SCORE = 0.02;
    let logTotal = 0;
    let weightSum = 0;
    for (const key of ["leftArm", "rightArm", "leftLeg", "rightLeg", "torso"]) {
        const weight = weights[key] * confidences[key];
        if (weight <= 0) {
            continue;
        }
        logTotal += weight * Math.log(Math.max(parts[key], MIN_PART_SCORE));
        weightSum += weight;
    }
    return {
        // Every part invisible means no evidence either way, not a zero score.
        total: weightSum > 1e-9 ? Math.exp(logTotal / weightSum) : 0,
        parts,
        confidence: weightSum,
        partConfidence: confidences,
    };
}
/**
 * Timing penalty for a player pose performed `offsetMs` after the reference.
 *
 * Positive offsets mean the player was late, which is the normal case: people
 * following a video react to it. The penalty is therefore centred on
 * `expectedLagMs`, not on zero, and is asymmetric, because falling further
 * behind is more forgivable than anticipating a move.
 */
export function timingPenalty(offsetMs, config = DEFAULT_SCORING_CONFIG) {
    const deviation = offsetMs - config.expectedLagMs;
    const sigma = deviation >= 0 ? config.lateSigmaMs : config.earlySigmaMs;
    return gaussianSimilarity(Math.abs(deviation), sigma);
}
/**
 * Best-matching offset ignoring the timing penalty, in ms.
 *
 * The normal score deliberately prefers candidates near `expectedLagMs`, so
 * its reported offset clusters around whatever lag is configured and cannot be
 * used to check that value. This search weighs pose similarity alone, so the
 * answer is independent of the assumption being tested.
 *
 * Returns undefined when no candidate could be scored. Accuracy depends on the
 * dance actually changing within the window: for slow movement several
 * candidates look alike and the estimate is correspondingly vague.
 */
export function measureTimingOffsetMs(input) {
    const config = input.config ?? DEFAULT_SCORING_CONFIG;
    const flattened = {
        ...config,
        // Wide enough to be flat across the search window.
        earlySigmaMs: 1e6,
        lateSigmaMs: 1e6,
        // Search far wider than the game scores over. Reusing the scoring window
        // censors the result: a player lagging more than `timingWindowMs` has
        // every sample reported at the window edge, so the measurement silently
        // returns the boundary instead of the truth. A real session showed 55% of
        // samples piled up at exactly 400 ms for this reason.
        timingWindowMs: MEASUREMENT_WINDOW_MS,
    };
    const result = scoreSample({ ...input, config: flattened });
    return result.total > 0 ? result.timingOffsetMs : undefined;
}
/**
 * Half-width of the lag measurement search, in ms.
 *
 * Generous on purpose: this is a diagnostic, not the scoring path, and a
 * measurement that cannot exceed its own window cannot detect that the
 * configured lag is too small.
 */
export const MEASUREMENT_WINDOW_MS = 1200;
/**
 * Scores one reference sample against the best matching player pose.
 *
 * Searches a window centred on the expected lag and keeps the candidate with
 * the highest final score. Returning the best match, rather than the closest
 * in time, is what lets a player who is consistently a little late still score
 * well.
 */
export function scoreSample(input) {
    const config = input.config ?? DEFAULT_SCORING_CONFIG;
    const weights = input.weights ?? DEFAULT_BODY_PART_WEIGHTS;
    const emptyResult = {
        total: 0,
        timingOffsetMs: 0,
        confidence: 0,
        scorable: false,
        partConfidence: { leftArm: 0, rightArm: 0, leftLeg: 0, rightLeg: 0, torso: 0, motion: 0 },
        parts: { leftArm: 0, rightArm: 0, leftLeg: 0, rightLeg: 0, torso: 0, motion: 0 },
    };
    if (input.referenceLandmarks.length === 0) {
        return emptyResult;
    }
    // Mirror the reference, not the player: the dancer's left hand corresponds
    // to the player's right, and swapping semantics is the only way to compare
    // the correct limbs.
    const referenceLandmarks = input.mirrored
        ? mirrorLandmarks(input.referenceLandmarks)
        : input.referenceLandmarks;
    const referenceFeatures = extractPoseFeatures(referenceLandmarks);
    if (!referenceFeatures) {
        return emptyResult;
    }
    const referenceMotion = extractMotionFeatures(input.mirrored
        ? input.referenceHistory.map((entry) => ({
            tMs: entry.tMs,
            landmarks: entry.landmarks.length > 0 ? mirrorLandmarks(entry.landmarks) : entry.landmarks,
        }))
        : input.referenceHistory, input.referenceTMs, config);
    const windowStart = input.referenceTMs + config.expectedLagMs - config.timingWindowMs;
    const windowEnd = input.referenceTMs + config.expectedLagMs + config.timingWindowMs;
    // Evaluate the candidates actually present in the window, plus its centre and
    // edges, so a sparse player stream still gets a fair look.
    const candidateTimes = new Set();
    for (const entry of input.playerHistory) {
        if (entry.tMs >= windowStart && entry.tMs <= windowEnd) {
            candidateTimes.add(entry.tMs);
        }
    }
    candidateTimes.add(input.referenceTMs + config.expectedLagMs);
    let best;
    // Tracked separately on the 0-1 scale. `ScoreResult.total` is reported as
    // 0-100, and comparing against that directly would mean every candidate
    // after the first loses, leaving the earliest pose to win by accident.
    let bestFinalScore = Number.NEGATIVE_INFINITY;
    for (const candidateTMs of candidateTimes) {
        const playerLandmarks = interpolateLandmarksAt(input.playerHistory, candidateTMs);
        if (!playerLandmarks || playerLandmarks.length === 0) {
            continue;
        }
        const playerFeatures = extractPoseFeatures(playerLandmarks);
        if (!playerFeatures) {
            continue;
        }
        const staticScore = scoreStaticPose(playerFeatures, referenceFeatures, weights, config.minVisibility);
        const playerMotion = extractMotionFeatures(input.playerHistory, candidateTMs, config);
        // With no motion data on either side there is nothing to compare, so fall
        // back to the static score rather than inventing a penalty.
        const motionScore = playerMotion && referenceMotion ? scoreMotion(playerMotion, referenceMotion) : undefined;
        const motionKeys = ["leftWrist", "rightWrist", "leftAnkle", "rightAnkle"];
        const motionConfidence = playerMotion && referenceMotion
            ? motionKeys.reduce((total, key) => total +
                (Math.min(playerMotion.confidence[key], referenceMotion.confidence[key]) >=
                    config.minVisibility
                    ? Math.min(playerMotion.confidence[key], referenceMotion.confidence[key])
                    : 0), 0) / motionKeys.length
            : 0;
        const staticWeight = config.staticPoseWeight * staticScore.confidence;
        const motionWeight = config.motionWeight * motionConfidence;
        const evidenceWeight = staticWeight + motionWeight;
        if (evidenceWeight <= 1e-9) {
            continue;
        }
        const rawScore = (staticWeight * staticScore.total + motionWeight * (motionScore ?? 0)) / evidenceWeight;
        const offsetMs = candidateTMs - input.referenceTMs;
        const finalScore = rawScore * timingPenalty(offsetMs, config);
        if (finalScore > bestFinalScore) {
            bestFinalScore = finalScore;
            best = {
                total: finalScore * 100,
                timingOffsetMs: offsetMs,
                confidence: evidenceWeight,
                scorable: true,
                partConfidence: {
                    ...staticScore.partConfidence,
                    motion: motionConfidence,
                },
                parts: {
                    leftArm: staticScore.parts.leftArm * 100,
                    rightArm: staticScore.parts.rightArm * 100,
                    leftLeg: staticScore.parts.leftLeg * 100,
                    rightLeg: staticScore.parts.rightLeg * 100,
                    torso: staticScore.parts.torso * 100,
                    motion: (motionScore ?? 0) * 100,
                },
            };
        }
    }
    return best ?? emptyResult;
}
//# sourceMappingURL=scoring.js.map